import React, { useEffect, useState, useContext } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';

import WithRouter from '../utilities/WithRouter';
import {
  setCurrentCampus,
  setCurrentBuilding,
  setCurrentZone,
  setCurrentZoneData,
  setCurrentGX,
  updateCurrentCampusData,
} from '../actions';

import { AppContext } from '../context/AppContext';
import { useCampusMeta } from '../context/CampusContextProvider';

import CurrentWeather from '../components/CurrentWeather';
import Breadcrumbs from '../components/Breadcrumbs';
import AuthenticatedComponent from '../utilities/AuthenticatedComponent';

import ComfortIcon from '../components/icons/ComfortIcon';
import icon_building from '../assets/icon_building.svg';
import ZoneCard from '../components/ZoneCard/ZoneCard';
import ZoneSettings from './subsections/ZoneSettings';
import { MarketingMessageThermalZones } from '../components/MarketingMessage/MarketingMessageThermalZones';
import { Header } from '../components/Header/Header';
import { Breadcrumb } from '../components/Breadcrumb/Breadcrumb';

const Comfort = ({
  router,
  currentCampusId,
  currentCampusData,
  updateCampusCallback,
  currentBuildingId,
  currentZoneId,
  currentZoneData,
  zoneSummaryCallback,
  onBuildingChange,
  onGXChange,
  onZoneChange,
  showMobile,
  openScheduleModal,
  allScheduleCallback,
  currentScheduleData,
  activePage,
  processing,
  setProcessing,
  errorMessage,
  setErrorMessage,
}) => {
  const { appConfig } = useContext(AppContext);
  const campusMeta = useCampusMeta();

  const [zoneHealth, setZoneHealth] = useState(null);
  const [sortedCampusBuildings, setSortedCampusBuildings] = useState([]);
  const hasNoZones = appConfig ? appConfig?.sections?.comfort?.hasZones === false : true;

  const campusName = campusMeta ? campusMeta.campusName : '';

  const authenticatedComponent = new AuthenticatedComponent();

  useEffect(() => {
    if (currentCampusData && currentCampusData.buildings) {
      getBuildingsArray();
    }
  }, []);

  // When device data updates, reload
  useEffect(() => {
    if (currentZoneId === null) {
      zoneSummaryCallback(null);
    }

    if (hasNoZones) {
      return;
    }
    // Device data update every 5 seconds
    if (currentCampusData) {
      if (currentCampusData.buildings) {
        getBuildingsArray();
      }
      if (currentZoneId === null) {
        let interval = setInterval(() => {
          authenticatedComponent.updateCampusData(currentCampusId, updateCampusCallback);
        }, 5000);
        return () => clearInterval(interval);
      }
    }
  }, [currentCampusData, currentZoneId, hasNoZones]);

  useEffect(() => {
    authenticatedComponent.updateCampusData(currentCampusId, updateCampusCallback);
  }, [currentZoneId === null]);

  const getBuildingsArray = () => {
    let tempBuildings = [];

    tempBuildings = currentCampusData.buildings.sort((a, b) => {
      if (a.buildingDescription.buildingEntityKey - b.buildingDescription.buildingEntityKey) {
        return 1;
      }
      if (a.buildingDescription.buildingEntityKey < b.buildingDescription.buildingEntityKey) {
        return -1;
      }
      return 0;
    });

    for (let i = 0; i < tempBuildings.length; i++) {
      tempBuildings[i].thermalZones.sort((a, b) => {
        if (a.longThermalZoneDisplayName - b.longThermalZoneDisplayName) {
          return 1;
        }
        if (a.longThermalZoneDisplayName < b.longThermalZoneDisplayName) {
          return -1;
        }
        return 0;
      });
    }

    setSortedCampusBuildings(tempBuildings);
  };

  const navigateWithValidation = (newPath) => {
    if (router.location.pathname !== newPath) {
      router.navigate(newPath);
    }
  };

  const selectZone = (selectedZoneId, buildingId) => {
    if (selectedZoneId) {
      setCurrentZone(selectedZoneId);
      setCurrentBuilding(buildingId);
      setCurrentGX(null);
      setCurrentZoneData(null);

      navigateWithValidation(
        '/comfort/' +
          currentCampusData.campusDescription.campusEntityKey +
          '/' +
          buildingId +
          '/' +
          selectedZoneId
      );

      authenticatedComponent.getZoneSummary(selectedZoneId, zoneSummaryCallback);
    }
  };

  const renderHeaderLockup = () => {
    return (
      <>
        <ComfortIcon
          backgroundFill="none"
          fillColor={'#02222B'}
          strokeColor={'#02222B'}
          strokeWidth={2.5}
          width="18px"
        />
        <h1>Comfort</h1>
      </>
    );
  };

  return (
    <ComfortSection className={showMobile ? 'comfort-section mobile' : 'comfort-section desktop'}>
      <Header siteName={campusName} showWeather={currentCampusData}>
        <Breadcrumb iconHeader={renderHeaderLockup()} />
      </Header>

      <div className="comfort-content">
        {hasNoZones ? (
          <MarketingMessageThermalZones />
        ) : currentCampusData && currentZoneId === null ? (
          <>
            {sortedCampusBuildings.map((building, index) => (
              <React.Fragment key={`building-${index}`}>
                <div className="building-container">
                  <div className="building-header">
                    <img src={icon_building} alt="building icon" width="20" height="20" />
                    <h3 className="building-name">
                      {building.buildingDescription.longBuildingDisplayName}
                    </h3>
                  </div>

                  {building.thermalZones.length !== 0 ? (
                    <div className="thermal-zones">
                      <table>
                        <tbody>
                          <tr className="labels">
                            <th className="head">Location</th>
                            <th className="head">Status</th>
                            <th className="head">
                              Temp{' '}
                              <span style={{ fontSize: '11px', color: '#62797e' }}>(Settings)</span>
                            </th>
                            <th className="head">Schedule</th>
                            <th className="head">Activity</th>
                            <th className="head">Keypad</th>
                            <th className="head">Managed</th>
                          </tr>

                          {building.thermalZones.map((zoneData, index) => (
                            <React.Fragment key={`${zoneData.shortThermalZoneDisplayName}${index}`}>
                              <ZoneCard
                                zoneRealtimeData={zoneData}
                                selectZone={() =>
                                  selectZone(
                                    zoneData.thermalZoneEntityKey,
                                    building.buildingDescription.buildingEntityKey
                                  )
                                }
                                setZoneHealth={setZoneHealth}
                              />
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="building-empty-state">
                      <ComfortIcon
                        backgroundFill="none"
                        fillColor={'#02222B'}
                        strokeColor={'#02222B'}
                        strokeWidth={2.5}
                        width="18px"
                      />

                      <p>There are no zones to display.</p>
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}
          </>
        ) : (
          <>
            <ZoneSettings
              currentCampusId={currentCampusId}
              currentZoneId={currentZoneId}
              currentZoneData={currentZoneData}
              zoneSummaryCallback={zoneSummaryCallback}
              allScheduleCallback={allScheduleCallback}
              currentScheduleData={currentScheduleData}
              showMobile={showMobile}
              openScheduleModal={openScheduleModal}
              zoneHealth={zoneHealth}
              setZoneHealth={setZoneHealth}
              processing={processing}
              setProcessing={setProcessing}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
            />
          </>
        )}
      </div>
    </ComfortSection>
  );
};

const ComfortSection = styled.section`
  color: #02222b;
  overflow: auto;

  .building-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0px 2px 6px #02222b14;
    display: flex;
    flex-flow: column;
    margin-bottom: 24px;
    padding: 24px 24px 12px;
    row-gap: 12px;

    .building-header {
      align-items: center;
      display: flex;
      padding: 0 0 12px 12px;

      h3 {
        font-size: 14px;
        font-weight: 600;
        margin: 0 0 0 12px;
      }
    }

    .building-empty-state {
      align-items: center;
      display: flex;
      gap: 12px;
      justify-content: center;
      padding: 26px 24px 24px;
    }

    //ZONE BUTTON ONLY ON MAIN COMFORT PAGE
    .thermal-zones .location {
      cursor: pointer;
      text-decoration: underline;
      transition: ease-in-out all 250ms;

      &:hover {
        color: rgba(2, 34, 43, 0.5);
        transition: ease-in-out all 250ms;
      }
    }

    table {
      border-collapse: collapse;
      font-size: 14px;
      text-align: left;
      width: 100%;

      th:nth-of-type(1),
      td:nth-of-type(1) {
        max-width: 220px;
        width: 20%;
      }

      th:nth-of-type(2),
      td:nth-of-type(2) {
        max-width: 130px;
        width: 16%;
      }

      th:nth-of-type(4),
      td:nth-of-type(4) {
        max-width: 130px;
        width: 12%;
      }
      th:nth-of-type(3),
      td:nth-of-type(3) {
        max-width: 140px;
        width: 12%;
      }

      th:nth-of-type(5),
      td:nth-of-type(5),
      th:nth-of-type(6),
      td:nth-of-type(6) {
        max-width: 60px;
        width: 5%;
      }

      th:nth-of-type(7),
      td:nth-of-type(7) {
        max-width: 130px;
        width: 14%;
      }

      th {
        color: #02222b;
        font-weight: 500;
        padding: 0.625em 0.25em;
      }

      td {
        padding: 0.25em;
      }

      tr {
        align-items: center;
        border-bottom: 1px solid #edf1f2;
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
      }

      tr:last-of-type {
        border-bottom: none;
      }
    }
  }

  @media screen and (max-width: 900px) {
    .header-container .header {
      padding-bottom: 0;
    }

    .thermal-zones tr.labels {
      display: none;
    }

    .building-container .thermal-zones .location {
      max-width: 175px;
      width: 35%;
    }

    .building-container table td.temp-range {
      width: 100px;
      max-width: unset !important;
    }
  }

  @media screen and (max-width: 1024px) {
    th:nth-of-type(2),
    td:nth-of-type(2) {
      display: none;
    }
  }

  .comfort-content td.status .thermal-zone-status-pill {
    width: 90%;
  }
`;

const mapStateToProps = (state) => {
  return {
    campuses: state.campuses,
    currentCampusId: state.currentCampusId,
    currentBuildingId: state.currentBuildingId,
    currentZoneId: state.currentZoneId,
    currentGXId: state.currentGXId,
    currentCampusData: state.currentCampusData,
    setCurrentZoneData: state.currentZoneData,
    currentUserData: state.currentUserData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setCurrentCampus: (campusId) => {
      dispatch(setCurrentCampus(campusId));
    },
    setCurrentBuilding: (buildingId) => {
      dispatch(setCurrentBuilding(buildingId));
    },
    setCurrentZone: (zoneId) => {
      dispatch(setCurrentZone(zoneId));
    },
    setCurrentZoneData: (zoneId) => {
      dispatch(setCurrentZoneData(zoneId));
    },
    setCurrentGX: (gxId) => {
      dispatch(setCurrentGX(gxId));
    },
    updateCurrentCampusData: (campusData) => {
      dispatch(updateCurrentCampusData(campusData));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WithRouter(Comfort));
